import { Injectable } from '@angular/core';
import { AppKeyInterface } from '@galaxy/marketplace-apps';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { capitalize } from '../../../../../common/components/capitalize.pipe';
import { AutomationErrorHandlerService } from '../../../../services';
import { CommonDisplayActionService } from '../../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../../common/common-display-activity.service';
import { EMPTY_PLACEHOLDER } from '../product-activations/constants';
import { ProductActivationService } from '../product-activations/product-activation.service';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example {@link AccountGroupCreatedService}
 */
@Injectable({ providedIn: 'root' })
export class ProductDeactivatedService implements CommonDisplayActionService, CommonDisplayActivityService {
  constructor(
    private readonly productActivationService: ProductActivationService,
    private readonly translateService: TranslateService,
    private readonly errorHandler: AutomationErrorHandlerService,
  ) {}

  getTemplateActionSubtitle(
    _namespace: string,
    step: WorkflowStepInterface,
    isWaitUntil?: boolean,
  ): Observable<string> {
    return this.getActionSubtitleEmptyState(step, isWaitUntil);
  }

  getActionSubtitle(namespace: string, step: WorkflowStepInterface, isWaitUntil?: boolean): Observable<string> {
    const appKey = this.productActivationService.getAppKeyFromStep(step);
    const addon = this.productActivationService.getAddonFromStep(step);
    // These triggers were originally saved with empty value meaning Any Product
    // This is a lazy migration to still save Any in that way, but allow for an EMPTY selection as well.
    if (!appKey.appId || appKey.appId === EMPTY_PLACEHOLDER) {
      if (appKey.appId === EMPTY_PLACEHOLDER) {
        this.errorHandler.setValidationError({
          stepId: step?.id,
          taskDefinitionId: step?.taskDefinitionId,
          errorMessage: this.translateService.instant(
            'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.CONFIG_ERROR_TEXT',
          ),
          isNonDismissable: true,
        });
      }

      return this.getActionSubtitleEmptyState(step, isWaitUntil);
    }

    let textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.PRODUCT_DEACTIVATED.WHEN_ACTION';
    if (isWaitUntil) {
      textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.PRODUCT_DEACTIVATED.WAIT_UNTIL_ACTION';
    }

    return this.productActivationService.getProductName(namespace, '', appKey, false, addon).pipe(
      switchMap((pName) => {
        return this.getActionSubtitleSuccessState({ textKey, pName });
      }),
      catchError(() => {
        const pName = this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.PRODUCT_OR_ADDON');
        return this.getActionSubtitleErrorState({ textKey, pName }, step);
      }),
    );
  }

  getActionSubtitleSuccessState({ textKey, pName }: any): Observable<string> {
    return this.translateService.stream(textKey, { productName: capitalize(pName) });
  }

  getActionSubtitleErrorState({ textKey, pName }: any, step: WorkflowStepInterface): Observable<string> {
    this.errorHandler.setValidationError({
      stepId: step?.id,
      taskDefinitionId: step?.taskDefinitionId,
      errorMessage: this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.CONFIG_ERROR_TEXT'),
      isNonDismissable: true,
    });

    return this.translateService.stream(textKey, { productName: pName });
  }

  // No errors because empty state is 'Any product' activated
  getActionSubtitleEmptyState(_step: WorkflowStepInterface, isWaitUntil?: boolean): Observable<string> {
    let textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.PRODUCT_DEACTIVATED.WHEN_ACTION';
    if (isWaitUntil) {
      textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.PRODUCT_DEACTIVATED.WAIT_UNTIL_ACTION';
    }

    const pName = this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.PRODUCT_OR_ADDON');

    return this.translateService.stream(textKey, { productName: pName });
  }

  getActivitySubtitle(namespace: string, activity: ActivityInterface): Observable<string> {
    const triggerData = activity?.triggerData ? JSON.parse(activity.triggerData) : {};
    let addon: AppKeyInterface;
    if (triggerData.addon_id) {
      addon = { appId: triggerData.addon_id };
    }
    return this.productActivationService
      .getProductName(namespace, '', { appId: triggerData.app_id, editionId: triggerData.edition_id }, true, addon)
      .pipe(
        switchMap((name) =>
          this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.PRODUCT_DEACTIVATED.ACTIVITY', {
            productName: capitalize(name),
          }),
        ),
      );
  }

  getActivityTitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.ENTERED_AUTOMATION');
  }
}
